import React from "react";
import { Link } from "react-router-dom";
import { TagType } from "../../types";
import { imageIdToURL, avatarUrl } from "../../utils/LinkUtils";
import { useGroq } from "../../lib/sanity";
import Tag from "./Tag";

const GET_ARTICLE_QUERY = `{
  "article": *[_type == "article"] | order(publishDate desc)[0...1]{
    title,
    subtitle,
    "publish_date": string::split(publishDate, "T")[0],
    "slug": slug.current,
    photo,
    "tags": tags[]->{text, color},
    "authors": authors->{ "first_name": firstName, "last_name": lastName, avatar, "id": _id }
  }
}`;

function BlogHero() {
  let { loading, error, data } = useGroq<any>(GET_ARTICLE_QUERY);
  return (
    <section className="relative">
      {/* Background image */}
      <div className="absolute inset-0 h-[32rem] pt-16 box-content">
        <img
          className="absolute inset-0 w-full h-full object-cover opacity-25"
          src={require("../assets/hero-bg.jpg")}
          alt="About"
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-white dark:from-gray-900"
          aria-hidden="true"
        ></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        <div className="pt-32 pb-12 md:pt-40 md:pb-16">
          {/* Featured article */}
          <div className="" data-aos="fade-down">
            <article className="max-w-sm mx-auto md:max-w-none grid md:grid-cols-2 gap-6 md:gap-8 lg:gap-12 xl:gap-16 items-center">
              <Link
                to={
                  !loading && !error && data?.article[0]
                    ? `/news/${data.article[0].publish_date}/${data.article[0].slug}`
                    : "/"
                }
                className="relative block group"
                data-aos="fade-right"
                data-aos-delay="200"
              >
                <div
                  className="absolute inset-0 bg-gray-800 hidden md:block transform md:translate-y-2 md:translate-x-4 xl:translate-y-4 xl:translate-x-8 group-hover:translate-x-0 group-hover:translate-y-0 transition duration-700 ease-out pointer-events-none"
                  aria-hidden="true"
                ></div>
                <figure className="relative h-0 pb-9/16 md:pb-3/4 lg:pb-9/16 overflow-hidden transform md:-translate-y-2 xl:-translate-y-4 group-hover:translate-x-0 group-hover:translate-y-0 transition duration-700 ease-out">
                  {!loading && !error && data?.article[0] ? (
                    <img
                      className="absolute inset-0 w-full h-full object-cover transform hover:scale-105 transition duration-700 ease-out"
                      src={imageIdToURL(data.article[0]?.photo)}
                      width="540"
                      height="303"
                      alt="Blog post 01"
                    />
                  ) : (
                    <div
                      className="relative h-0 pb-9/16 md:pb-3/4 lg:pb-9/16 overflow-hidden transform md:-translate-y-2 xl:-translate-y-4 group-hover:translate-x-0 group-hover:translate-y-0 transition duration-700 ease-out bg-gray-600 animate-pulse"
                      style={{ width: "540px", height: "303px" }}
                    ></div>
                  )}
                </figure>
              </Link>
              <div data-aos="fade-left" data-aos-delay="200">
                <header>
                  <div className="mb-3">
                    <ul className="flex flex-wrap text-xs font-medium -m-1">
                      {!loading && !error && data?.article[0] && data.article[0].tags
                        ? data.article[0].tags.map((tag: TagType, i: number) => {
                            return (
                              <Tag
                                color={tag.color}
                                text={tag.text}
                                key={"herotags" + i}
                              />
                            );
                          })
                        : null}
                    </ul>
                  </div>
                  {!loading && !error && data?.article[0] ? (
                    <h3 className="h3 text-2xl lg:text-3xl mb-2">
                      <Link
                        to={`/news/${data.article[0].publish_date}/${data.article[0].slug}`}
                        className="text-4xl font-header font-bold mb-4 text-white leading-tight"
                      >
                        {data.article[0].title}
                      </Link>
                    </h3>
                  ) : (
                    <div className="w-full mb-4 bg-gray-600 animate-pulse h-16"></div>
                  )}
                </header>
                {!loading && !error && data?.article[0] ? (
                  <p className="text-lg text-gray-400 flex-grow">
                    {data.article[0].subtitle}
                  </p>
                ) : (
                  <>
                    <div className="w-full bg-gray-600 h-6 mb-2 animate-pulse"></div>
                    <div className="w-full bg-gray-600 h-6 mb-2 animate-pulse"></div>
                    <div className="w-1/3 bg-gray-600 h-6 animate-pulse"></div>
                  </>
                )}
                <footer className="flex items-center mt-4">
                  <Link
                    to={
                      !loading && !error && data?.article[0]
                        ? `/news/${data.article[0].publish_date}/${data.article[0].slug}`
                        : "/"
                    }
                  >
                    {" "}
                    {/*TODO: Change to link to profile*/}
                    <img
                      className="rounded-full flex-shrink-0 mr-4"
                      src={
                        // if the page is not loading and article has author
                        !loading && !error && data?.article[0]?.authors
                          ? data.article[0]?.authors?.avatar
                            ? imageIdToURL(data.article[0].authors.avatar)
                            : avatarUrl([data.article[0].authors.first_name, data.article[0].authors.last_name].filter(Boolean).join(" "))
                          : avatarUrl("Brown Esports")
                      }
                      width="40"
                      height="40"
                      alt="Author 04"
                    />
                  </Link>
                  <div>
                    <Link
                      to={
                        data?.article[0]?.authors?.id
                          ? `/author/${data.article[0].authors.id}`
                          : "#"
                      }
                      className="font-medium text-gray-200 hover:text-gray-100 transition duration-150 ease-in-out"
                    >
                      {!loading && !error && data?.article[0]?.authors
                        ? [data.article[0].authors.first_name, data.article[0].authors.last_name].filter(Boolean).join(" ")
                        : "Brown Esports"}
                    </Link>
                    <span className="text-gray-500"> · </span>
                    <span className="text-gray-400">
                      {!loading && !error && data?.article[0]
                        ? new Date(
                            data.article[0].publish_date
                          ).toLocaleDateString("en-us", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            timeZone: "UTC",
                          })
                        : null}
                    </span>
                  </div>
                </footer>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}

export default BlogHero;
